import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useRef, useState } from "react";
import {
  ApiKeyAdminCsvImportKeyOutcome,
  ApiKeyAdminCsvImportOrgOutcome,
  type ApiKeyAdminCsvImportReportFieldsFragment,
  ApiKeyAdminListDocument,
  useApiKeyAdminImportCsvMutation,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../throw-on-graphql-errors";

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
  tableBox: {
    maxHeight: 320,
    overflow: "auto",
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
  } satisfies SxProps<Theme>,
  dropzone: {
    py: 3,
    px: 2,
    border: "2px dashed",
    borderColor: "divider",
    borderRadius: 1,
    textAlign: "center",
  } satisfies SxProps<Theme>,
};

type ImportCsvDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
};

const outcomeColor = (
  outcome: ApiKeyAdminCsvImportOrgOutcome,
): "success" | "info" | "warning" | "error" => {
  switch (outcome) {
    case ApiKeyAdminCsvImportOrgOutcome.Ok: {
      return "success";
    }
    case ApiKeyAdminCsvImportOrgOutcome.AlreadyLinked: {
      return "info";
    }
    case ApiKeyAdminCsvImportOrgOutcome.NotFound: {
      return "warning";
    }
    case ApiKeyAdminCsvImportOrgOutcome.Stale: {
      return "warning";
    }
    case ApiKeyAdminCsvImportOrgOutcome.Ambiguous: {
      return "error";
    }
  }
};

const keyOutcomeColor = (
  outcome: ApiKeyAdminCsvImportKeyOutcome,
): "success" | "info" | "error" => {
  switch (outcome) {
    case ApiKeyAdminCsvImportKeyOutcome.Create: {
      return "success";
    }
    case ApiKeyAdminCsvImportKeyOutcome.Merge: {
      return "info";
    }
    case ApiKeyAdminCsvImportKeyOutcome.Conflict: {
      return "error";
    }
  }
};

/**
 * Two-phase CSV import dialog.
 *
 * The admin uploads a CSV; the backend returns a preview (`dryRun=true`)
 * that's rendered as a row-by-row report. If the preview is acceptable,
 * the admin clicks "Confirm import" to re-run the same logic with
 * `dryRun=false` inside a transaction.
 */
const ImportCsvDialog = ({ open, onClose }: ImportCsvDialogProps) => {
  const [filename, setFilename] = useState<string | null>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [preview, setPreview] =
    useState<ApiKeyAdminCsvImportReportFieldsFragment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [runImport, { loading }] = useApiKeyAdminImportCsvMutation({
    refetchQueries: [ApiKeyAdminListDocument],
  });

  const reset = () => {
    setFilename(null);
    setCsvText(null);
    setPreview(null);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = async (file: File) => {
    setError(null);
    setPreview(null);
    try {
      const text = await file.text();
      setFilename(file.name);
      setCsvText(text);
      const result = await runImport({
        variables: { input: { csvText: text, dryRun: true } },
      });
      throwOnGraphqlErrors(result);
      const report = result.data?.apiKeyAdminImportCsv;
      if (report !== null && report !== undefined) {
        setPreview(report);
      } else {
        setError("The server did not return an import preview. Please retry.");
      }
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    }
  };

  const handleConfirm = async () => {
    if (csvText === null) {
      return;
    }
    setError(null);
    try {
      const result = await runImport({
        variables: { input: { csvText, dryRun: false } },
      });
      throwOnGraphqlErrors(result);
      const report = result.data?.apiKeyAdminImportCsv;
      if (report !== null && report !== undefined) {
        setPreview(report);
      } else {
        setError("The server did not return an import result. Please retry.");
      }
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    }
  };

  const previewHasConflict =
    preview?.keyOutcome === ApiKeyAdminCsvImportKeyOutcome.Conflict;

  return (
    <Dialog fullWidth open={open} maxWidth="md" onClose={handleClose}>
      <DialogTitle>Import API key from CSV</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          {preview === null && (
            <Box sx={styles.dropzone}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file !== undefined) {
                    void handleFile(file);
                  }
                }}
              />
              <Typography gutterBottom variant="body2" color="text.secondary">
                Select a CSV exported from another environment.
              </Typography>
              <Button
                variant="contained"
                disabled={loading}
                onClick={() => {
                  fileInputRef.current?.click();
                }}
              >
                {loading ? "Parsing…" : "Choose file"}
              </Button>
              {filename !== null && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  {filename}
                </Typography>
              )}
            </Box>
          )}
          {preview !== null && (
            <>
              <Stack
                useFlexGap
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography variant="subtitle2">
                  Key: {preview.keyName}
                </Typography>
                <Chip
                  size="small"
                  label={preview.keyOutcome}
                  color={keyOutcomeColor(preview.keyOutcome)}
                />
                {preview.applied && (
                  <Chip
                    size="small"
                    label="APPLIED"
                    color="success"
                    variant="outlined"
                  />
                )}
              </Stack>
              {preview.keyMessage !== null &&
                preview.keyMessage !== undefined && (
                  <Alert severity={previewHasConflict ? "error" : "info"}>
                    {preview.keyMessage}
                  </Alert>
                )}
              <Typography variant="body2" color="text.secondary">
                {preview.orgsToLink} orgs will be linked.{" "}
                {preview.orgRows.length} CSV row(s) processed.
              </Typography>
              <Box sx={styles.tableBox}>
                <TableContainer>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Organization</TableCell>
                        <TableCell>Outcome</TableCell>
                        <TableCell>Message</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.orgRows.map((row) => (
                        <TableRow key={`${row.rowNumber}-${row.companyName}`}>
                          <TableCell>{row.rowNumber}</TableCell>
                          <TableCell>{row.companyName}</TableCell>
                          <TableCell>{row.organizationDisplayName}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.outcome}
                              color={outcomeColor(row.outcome)}
                            />
                          </TableCell>
                          <TableCell>{row.message}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
          {loading && preview !== null && (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleClose}>
          {preview?.applied === true ? "Close" : "Cancel"}
        </Button>
        {preview !== null && !preview.applied && (
          <Button
            disabled={loading}
            onClick={() => {
              reset();
            }}
          >
            Start over
          </Button>
        )}
        {preview !== null && !preview.applied && !previewHasConflict && (
          <Button
            disabled={loading}
            variant="contained"
            onClick={handleConfirm}
          >
            {loading ? "Importing…" : "Confirm import"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportCsvDialog;
